import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';
import { Analytics } from '@vercel/analytics/react';
import { AuthProvider } from './contexts/AuthContext';
import { SupabaseAuthProvider, useSupabaseAuth } from './contexts/SupabaseAuthContext';
import { LocalizationProvider } from './contexts/LocalizationContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { NotificationToast } from './components/shared/NotificationToast';
import { handleDeepLink } from './utils/deepLinking';
import AnimatedCompanyWebsite from './pages/Home';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import CandidatesPage from './pages/CandidatesPage';
import ServicesPage from './pages/ServicesPage';
import ClientPortalPage from './pages/ClientPortal';
import CandidatePortalPage from './pages/CandidatePortal';
import AdminPortalPage from './pages/AdminPortal';
import TestimonialsPage from './pages/TestimonialsPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import CookiePolicyPage from './pages/CookiePolicyPage';
import ModernSlaveryPage from './pages/ModernSlaveryPage';
import TermsOfService from './pages/TermsOfService';
import SitemapPage from './pages/SitemapPage';
import CookieConsentModal from './components/CookieConsentModal';

// New Supabase-based components
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import AuthCallback from './pages/AuthCallback';
import ProtectedRoute from './components/ProtectedRoute';

// Recruiter components
import RecruiterLayout from './components/recruiter/RecruiterLayout';
import RecruiterDashboard from './pages/RecruiterDashboard';
import CandidatesList from './components/recruiter/CandidatesList';
import CandidateDetail from './components/recruiter/CandidateDetail';
import RFQsList from './components/recruiter/RFQsList';
import RFQDetail from './components/recruiter/RFQDetail';
import RFQMatches from './pages/recruiter/RFQMatches';
import LinkedInLeadsView from './pages/recruiter/LinkedInLeadsView';
import ClientsList from './components/recruiter/ClientsList';
import ClientDetail from './components/recruiter/ClientDetail';
import ApplicationDetail from './components/recruiter/ApplicationDetail';
import CandidateRequests from './components/recruiter/CandidateRequests';
import SettingsLayout from './components/recruiter/settings/SettingsLayout';
import BulkUpload from './components/recruiter/settings/BulkUpload';
import SkillsManagement from './components/recruiter/settings/SkillsManagement';
import PositionsManagement from './components/recruiter/settings/PositionsManagement';
import Profile from './components/recruiter/settings/Profile';
import EmailSettings from './components/recruiter/settings/EmailSettings';
import Notifications from './components/recruiter/settings/Notifications';
import Integrations from './components/recruiter/settings/Integrations';
import MatchingSettings from './components/recruiter/settings/MatchingSettings';
import AutomationSettings from './components/recruiter/settings/AutomationSettings';
import CandidateRequestSettings from './components/recruiter/settings/CandidateRequestSettings';
import EmailTemplates from './components/recruiter/settings/EmailTemplates';
import PDFTemplates from './components/recruiter/settings/PDFTemplates';
import AISettings from './components/recruiter/settings/AISettings';
import LinkedInTemplates from './components/recruiter/settings/LinkedInTemplates';

// Candidate portal components
import CandidateLayout from './components/candidate/CandidateLayout';
import CandidateDashboard from './components/candidate/CandidateDashboard';
import MyDetails from './components/candidate/MyDetails';
import CandidateApplications from './components/candidate/CandidateApplications';
import CandidateJobBoard from './components/candidate/CandidateJobBoard';
import CandidateSettings from './components/candidate/CandidateSettings';
import CandidateOnboarding from './components/candidate/onboarding/CandidateOnboarding';

// Client portal components
import ClientLayout from './components/client/ClientLayout';
import ClientDashboard from './components/client/ClientDashboard';
import ClientMyDetails from './components/client/ClientMyDetails';
import ClientMyOrganisation from './components/client/ClientMyOrganisation';
import ClientCandidates from './components/client/ClientCandidates';
import ClientRequests from './components/client/ClientRequests';
import ClientSettings from './components/client/ClientSettings';
import ClientHelpSupport from './components/client/ClientHelpSupport';

// Public job board
import PublicJobListing from './pages/PublicJobListing';
import PublicJobApplication from './pages/PublicJobApplication';
import JobBoard from './pages/JobBoard';

// Notification inbox
import { NotificationInbox } from './components/shared/NotificationInbox';

// Mobile app handler
import MobileAppHandler from './components/MobileAppHandler';

// Deep linking handler component
function DeepLinkHandler() {
  const navigate = useNavigate();
  const { role } = useSupabaseAuth();

  useEffect(() => {
    // Listen for notification clicks (from push notifications)
    const handleNotificationClick = (event) => {
      if (event.detail && role) {
        handleDeepLink(event.detail, navigate, role);
      }
    };

    window.addEventListener('notification-clicked', handleNotificationClick);

    return () => {
      window.removeEventListener('notification-clicked', handleNotificationClick);
    };
  }, [navigate, role]);

  return null;
}

function App() {
  return (
    <AuthProvider>
      <SupabaseAuthProvider>
        <NotificationProvider>
          <LocalizationProvider>
            <Router>
              <Analytics />
              <MobileAppHandler />
              <DeepLinkHandler />
              <NotificationToast />
              <div>
                <Routes>
                {/* Public routes */}
                <Route path="/" element={<AnimatedCompanyWebsite />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/candidates" element={<CandidatesPage />} />
                <Route path="/services" element={<ServicesPage />} />
                <Route path="/testimonials" element={<TestimonialsPage />} />
                <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                <Route path="/cookie-policy" element={<CookiePolicyPage />} />
                <Route path="/modern-slavery-statement" element={<ModernSlaveryPage />} />
                <Route path="/terms-of-service" element={<TermsOfService />} />
                <Route path="/sitemap" element={<SitemapPage />} />

                {/* Universal login and password reset */}
                <Route path="/login" element={<Login />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/auth/callback" element={<AuthCallback />} />

                {/* Public Job Board and Listing Routes */}
                <Route path="/jobs" element={<JobBoard />} />
                <Route path="/job/:id" element={<PublicJobListing />} />
                <Route path="/job/:id/apply" element={<PublicJobApplication />} />

                {/* Protected Recruiter Routes */}
                <Route
                  path="/recruiter"
                  element={
                    <ProtectedRoute requiredRole="recruiter">
                      <RecruiterLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<RecruiterDashboard />} />
                  <Route path="candidates" element={<CandidatesList />} />
                  <Route path="candidates/:id" element={<CandidateDetail />} />
                  <Route path="rfqs" element={<RFQsList />} />
                  <Route path="rfqs/:id" element={<RFQDetail />} />
                  <Route path="rfqs/:id/matches" element={<RFQMatches />} />
                  <Route path="linkedin-leads/:jobId" element={<LinkedInLeadsView />} />
                  <Route path="applications/:id" element={<ApplicationDetail />} />
                  <Route path="clients" element={<ClientsList />} />
                  <Route path="clients/:id" element={<ClientDetail />} />
                  <Route path="requests" element={<CandidateRequests />} />
                  <Route path="notifications" element={<NotificationInbox />} />
                  <Route path="settings/*" element={
                    <SettingsLayout>
                      <Routes>
                        <Route index element={<BulkUpload />} />
                        <Route path="bulk-upload" element={<BulkUpload />} />
                        <Route path="skills" element={<SkillsManagement />} />
                        <Route path="positions" element={<PositionsManagement />} />
                        <Route path="profile" element={<Profile />} />
                        <Route path="email" element={<EmailSettings />} />
                        <Route path="notifications" element={<Notifications />} />
                        <Route path="integrations" element={<Integrations />} />
                        <Route path="matching" element={<MatchingSettings />} />
                        <Route path="automation" element={<AutomationSettings />} />
                        <Route path="candidate-requests" element={<CandidateRequestSettings />} />
                        <Route path="email-templates" element={<EmailTemplates />} />
                        <Route path="pdf-templates" element={<PDFTemplates />} />
                        <Route path="ai" element={<AISettings />} />
                        <Route path="linkedin-templates" element={<LinkedInTemplates />} />
                      </Routes>
                    </SettingsLayout>
                  } />
                </Route>

                {/* Candidate Onboarding (full-screen, no sidebar) */}
                <Route
                  path="/candidate/onboarding"
                  element={
                    <ProtectedRoute requiredRole="candidate">
                      <CandidateOnboarding />
                    </ProtectedRoute>
                  }
                />

                {/* Protected Candidate Routes */}
                <Route
                  path="/candidate"
                  element={
                    <ProtectedRoute requiredRole="candidate">
                      <CandidateLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<CandidateDashboard />} />
                  <Route path="my-details" element={<MyDetails />} />
                  <Route path="applications" element={<CandidateApplications />} />
                  <Route path="job-board" element={<CandidateJobBoard />} />
                  <Route path="notifications" element={<NotificationInbox />} />
                  <Route path="settings" element={<CandidateSettings />} />
                </Route>

                {/* Protected Client Routes */}
                <Route
                  path="/client"
                  element={
                    <ProtectedRoute requiredRole="client">
                      <ClientLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<ClientDashboard />} />
                  <Route path="my-details" element={<ClientMyDetails />} />
                  <Route path="my-organisation" element={<ClientMyOrganisation />} />
                  <Route path="candidates" element={<ClientCandidates />} />
                  <Route path="requests" element={<ClientRequests />} />
                  <Route path="notifications" element={<NotificationInbox />} />
                  <Route path="settings" element={<ClientSettings />} />
                  <Route path="help-support" element={<ClientHelpSupport />} />
                </Route>

                {/* Existing portals (kept for backward compatibility) */}
                <Route path="/client-portal-auth" element={<ClientPortalPage />} />
                <Route path="/candidate-portal-auth" element={<CandidatePortalPage />} />
                <Route path="/admin-portal-auth" element={<AdminPortalPage />} />
              </Routes>

              {/* Cookie consent modal appears on all pages */}
              <CookieConsentModal />
            </div>
          </Router>
        </LocalizationProvider>
      </NotificationProvider>
      </SupabaseAuthProvider>
    </AuthProvider>
  );
}

export default App;